// @flow
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Banner,
  Divider,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import messages from './messages';
import { partitionFindings } from './utils';
import type { NaurFinding } from '../../services/naurService';

type Props = {
  findings: ?Array<NaurFinding>,
};

const findingKey = (item: NaurFinding, index: number) =>
  `${item.code}:${item.buildingId ?? ''}:${item.unitId ?? ''}:${
    item.householdId ?? ''
  }:${index}`;

const FindingLocation = ({ finding }: { finding: NaurFinding }) => {
  if (!finding.buildingId) {
    return null;
  }

  const buildingName = finding.buildingName ?? finding.buildingId;

  return (
    <Typography variant="labelSmall" color="text.secondary">
      {finding.unitNumber ? (
        <FormattedMessage
          {...messages.findingLocationWithUnit}
          values={{ buildingName, unitNumber: finding.unitNumber }}
        />
      ) : (
        <FormattedMessage
          {...messages.findingLocation}
          values={{ buildingName }}
        />
      )}
    </Typography>
  );
};

const FindingRow = ({ finding }: { finding: NaurFinding }) => (
  <Stack direction="column" spacing={0.5}>
    <FindingLocation finding={finding} />
    <Typography variant="bodyMedium">{finding.message}</Typography>
  </Stack>
);

const FindingGroup = ({ findings }: { findings: NaurFinding[] }) => (
  <Stack direction="column" spacing={1.5} divider={<Divider />}>
    {findings.map((item, index) => (
      <FindingRow key={findingKey(item, index)} finding={item} />
    ))}
  </Stack>
);

/**
 * Renders the preflight payload, which the GET and the 422 both return verbatim.
 * BLOCKER items are prerequisites; everything else is context and never blocks.
 * `message` is display-ready, so it is printed as-is.
 */
const FindingsList = ({ findings }: Props) => {
  const { blockers, infos } = partitionFindings(findings);

  if (blockers.length === 0 && infos.length === 0) {
    return null;
  }

  return (
    <Stack direction="column" spacing={2}>
      {blockers.length > 0 && (
        <Banner
          status="error"
          message={
            <Stack direction="column" spacing={1.5}>
              <Typography variant="labelLarge">
                <FormattedMessage
                  {...messages.blockersHeading}
                  values={{ count: blockers.length }}
                />
              </Typography>
              <FindingGroup findings={blockers} />
            </Stack>
          }
        />
      )}
      {infos.length > 0 && (
        <Banner
          status="info"
          message={
            <Stack direction="column" spacing={1.5}>
              <Typography variant="labelLarge">
                <FormattedMessage
                  {...messages.infoHeading}
                  values={{ count: infos.length }}
                />
              </Typography>
              <FindingGroup findings={infos} />
            </Stack>
          }
        />
      )}
    </Stack>
  );
};

export default FindingsList;
